import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useD504Query, ItemState } from './hooks/useD504Query';
import { useD504Approval } from './hooks/useD504Approval';
import { useD504Permission } from './hooks/useD504Permission';
import { d504Api } from '../../services/d504Api';
import {
  BatchAcceptanceBridge,
  pwd504f1AcceptanceManifest,
} from '../../features/acceptance';
import type { AcceptanceManifest } from '../../features/acceptance';
import './D504Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};
const acceptanceActionIds = {
  list: 'PWD514F1-ACT-QUERY',
  approve: 'PWD514F1-ACT-APPROVE',
};
void acceptanceActionIds;

const fmtNumber = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

interface D504FormProps {
  api?: typeof d504Api;
  permissionModule?: string;
  manifest?: AcceptanceManifest;
}

const D504Form: React.FC<D504FormProps> = ({
  api = d504Api,
  permissionModule = 'pwd504f1',
  manifest = pwd504f1AcceptanceManifest,
}) => {
  const activeManifest = manifest;
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    queried,
    selectedCount,
    allSelected,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    resetItems,
  } = useD504Query(api);

  const { executing, handleExecute } = useD504Approval(items, resetItems, api);
  const { canApprove } = useD504Permission(permissionModule);

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    if (selectedCount === 0) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆託工入庫單？此操作不可復原。`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const someSelected = items.some((s) => s.selected) && !allSelected;

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          disabled={!record.item.can_approve}
          onChange={(e) =>
            handleToggleItem(record.item.fd140_01, e.target.checked)
          } data-contract-field="fd140_01"
        />
      ),
    },
    {
      title: <span data-contract-field="fd140_01">入庫單號</span>,
      dataIndex: ['item', 'fd140_01'],
      key: 'fd140_01',
      width: 120,
    },
    {
      title: <span data-contract-field="fd140_10">加工廠</span>,
      key: 'factory',
      width: 200,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fd140_10;
        const name = record.item.factory_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: <span data-contract-field="fd140_11">託工單號</span>,
      dataIndex: ['item', 'fd140_11'],
      key: 'fd140_11',
      width: 120,
    },
    {
      title: <span data-contract-field="fd140_12">入庫日期</span>,
      dataIndex: ['item', 'fd140_12'],
      key: 'fd140_12',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: <span data-contract-field="fd140_13">庫位</span>,
      dataIndex: ['item', 'fd140_13'],
      key: 'fd140_13',
      width: 80,
    },
    {
      title: <span data-contract-field="fd140_16">工繳總金額</span>,
      dataIndex: ['item', 'fd140_16'],
      key: 'fd140_16',
      width: 120,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: <span data-contract-field="fd140_21">總入庫量</span>,
      dataIndex: ['item', 'fd140_21'],
      key: 'fd140_21',
      width: 100,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
  ];

  return (
    <div className="d504-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                託工管理
              </a>
              <span>/</span>
              <span className="current">託工入庫單核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>託工入庫單核准</h1>
            <span className="badge">D504</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="query_receipt_start">入庫單號.從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <label className="separator" data-contract-field="query_receipt_end">至</label>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <span data-acceptance-action="PWD514F1-ACT-QUERY">
          <Button
            type="primary"
            onClick={onQuery}
            loading={loading}
            data-acceptance-action="PWD504-ACT-LIST-PENDING"
            data-acceptance-reveal-fields
          >
            確定範圍
          </Button>
          </span>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          <Table<ItemState>
            columns={columns}
            dataSource={items}
            rowKey={(record) => record.item.fd140_01}
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
            locale={{ emptyText: queried ? '查無資料' : '請輸入入庫單號範圍並點擊「確定範圍」查詢' }}
          />
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <span data-acceptance-action="PWD514F1-ACT-APPROVE">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing || !canApprove}
            loading={executing}
            onClick={onExecute}
            data-acceptance-action="PWD504-ACT-APPROVE"
          >
            存檔 ({selectedCount})
          </Button>
          </span>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={activeManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fd140_01)}
          keyOf={(item) => item.fd140_01}
          queryExact={async (key) => (await api.query(key, key)).items?.[0] || null}
          lastApiResponse={items}
          loading={loading || executing}
          onAutoFill={() => {
            const first = items.find(state => state.item.can_approve);
            if (first) handleToggleItem(first.item.fd140_01, true);
          }}
          autoFillDisabled={!items.some(state => state.item.can_approve)}
          autoFillReason="只選取正式 API 回傳的第一筆可核准託工入庫單。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default D504Form;
